import { GetStaticPropsContext } from 'next'
import { useLocale, useTranslations } from 'next-intl'
import { ReactElement, useMemo, useState, useRef, useEffect } from 'react'
import useSWR from 'swr'
import { NextPageWithLayout } from '../../common/common-types'
import { useChinaDetector } from '../../common/region-locale'
import PageLayout from '../../components/page-layout/page-layout'
import PdMedia from '../../components/pd-media/pd-media'
import PdStandaloneVideo from '../../components/pd-standalone-video/pd-standalone-video'
import retriveMessages, { LocaleType } from '../../messages/message'
import Image from 'next/image'
import { withSEO } from '../../common/with-seo'
import clsx from 'clsx'
import History from './history.component'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import Marquee from 'react-fast-marquee'
import WorldMap from './world-map.component'
import Count from './count.component'
import { AboutUsData, getAboutUs } from '@/service/about-us.service'
import { SEOData } from '@service/seo.service'
import { useMobileDetector } from '@/hooks/use-mobile-detector'
import PdImage from '@/components/pd-image/pd-image'
import Head from 'next/head'

gsap.registerPlugin(ScrollTrigger, useGSAP)

const About: NextPageWithLayout<{ initData: AboutUsData; seoData?: SEOData | null }> = ({ initData, seoData }) => {
  const isZH = useChinaDetector()
  const isMobileDevice = useMobileDetector()
  const locale = useLocale()
  const t = useTranslations()
  const [theme, setTheme] = useState<'white' | 'blue'>('white')
  const [isDomReady, setIsDomReady] = useState(false)
  const historyRef = useRef<HTMLDivElement>(null)
  const cultureRef = useRef<HTMLDivElement>(null)

  const { data: _data } = useSWR<AboutUsData>(`${locale}/api/front/v2/about-us`, () => getAboutUs(locale as string))
  const data = _data || initData

  const globalDistribution = useMemo(() => {
    return {
      title: data?.globalLayout?.title,
      tops: data?.globalLayout?.titleDesc.map((item) => ({
        count: item.title,
        desc: item.desc,
      })),
      mediaUrl: data?.globalLayout?.location.backgroudUrl || '',
      locationItems: data?.globalLayout?.location.locationItem || [],
      productMediaUrl: data?.globalLayout?.location.productMediaUrl || '',
      bottoms: data?.globalLayout?.location.locationDescList || [],
      brandDesc: data?.globalLayout?.bottomTitle || '',
    }
  }, [data])

  // 定义topBannerPic变量，根据isMobileDevice返回不同的图片地址
  const topBannerPic = useMemo(() => {
    if (!data) return ''
    return isMobileDevice ? data.topBannerPicH5Url : data.topBannerPicUrl
  }, [data, isMobileDevice])

  const [playIcon, setPlayIcon] = useState<string>('https://cdn.pudutech.com/official-website/about-us/btn_play.svg')
  const [playing, setPlaying] = useState<boolean>(false)

  function play() {
    setPlaying(true)
  }

  // 确保DOM完全渲染后再初始化ScrollTrigger
  useEffect(() => {
    // 使用requestAnimationFrame确保DOM渲染完成
    const timeoutId = setTimeout(() => {
      if (historyRef.current && cultureRef.current) {
        setIsDomReady(true)
      }
    }, 1000)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [data])

  // 页面卸载时清理ScrollTrigger实例
  useEffect(() => {
    return () => {
      ScrollTrigger.killAll()
    }
  }, [])

  useGSAP(() => {
    // 确保DOM准备就绪且数据加载完成后再初始化ScrollTrigger
    if (!isDomReady || !historyRef.current || !cultureRef.current || !data) return

    // 刷新ScrollTrigger以确保正确的触发点计算
    ScrollTrigger.refresh()
    const historyTrigger = gsap.timeline({
      scrollTrigger: {
        scroller: '#main',
        trigger: historyRef.current,
        start: 'top center',
        end: 'center center',
        onEnter: () => {
          setTheme('blue')
        },
        onLeaveBack: () => {
          setTheme('white')
        },
      },
    })

    const cultureTrigger = gsap.timeline({
      scrollTrigger: {
        scroller: '#main',
        trigger: cultureRef.current,
        start: 'top center',
        end: 'center center',
        onEnter: () => {
          setTheme('white')
        },
        onLeaveBack: () => {
          setTheme('blue')
        },
      },
    })

    return () => {
      historyTrigger.kill()
      cultureTrigger.kill()
    }
  }, [data, isDomReady])

  const isBlueTheme = useMemo(() => {
    return theme === 'blue'
  }, [theme])

  return (
    <>
      <Head>
        <title>{seoData?.title || data?.tabTitle || t('Title')}</title>
      </Head>
      <div
        className={clsx(
          'w-full h-fit',
          'transition-all duration-600 ease-in-out',
          isBlueTheme ? 'bg-pdblue' : 'bg-white'
        )}
      >
        <div
          className={['laptop:h-150', 'aspect-square', 'relative w-full'].join(' ')}
          style={{
            aspectRatio: '1 / 1',
          }}
        >
          <PdMedia type="image" src={topBannerPic} mask={true} useLoader={true}>
            <img
              src={playIcon}
              className="absolute block w-12 h-12 transition-all hover:scale-116 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
              alt="play"
              onMouseEnter={() => setPlayIcon('https://cdn.pudutech.com/official-website/about-us/btn_play_hover.svg')}
              onMouseLeave={() => setPlayIcon('https://cdn.pudutech.com/official-website/about-us/btn_play.svg')}
              onClick={() => play()}
            />
          </PdMedia>
          {
            <PdStandaloneVideo
              source={data?.topBannerMediaUrl || ''}
              show={playing}
              onClosed={() => setPlaying(false)}
            />
          }
        </div>

        {/* About PUDU */}
        <div
          className={['pt-9 pb-20 px-6', 'laptop:pt-16 laptop:pb-30 laptop:px-0', 'relative w-full bg-white'].join(' ')}
        >
          <div className={['flex-col', 'laptop:flex-row', 'w-full max-w-300 mx-auto flex'].join(' ')}>
            <h2
              className={[
                'w-full text-28 pb-3',
                'laptop:w-100 laptop:text-32 laptop:pb-0',
                'flex-initial mt-4 text-black/90 font-semibold',
              ].join(' ')}
            >
              {data?.aboutPuduTitle}
            </h2>
            <div className={['w-full', 'laptop:w-200', 'flex-initial pt-4 border-t border-c2'].join(' ')}>
              <p className="whitespace-pre-wrap text-base text-black/60 font-normal">{data?.aboutPuduContent}</p>
              {!!data?.companyProfile?.length && (
                <dl className="mt-10 flex flex-col gap-8 laptop:mt-12">
                  {data.companyProfile.map((item, index) => (
                    <div key={`${item.label}-${index}`}>
                      <dt className="border-b border-c2 pb-3 text-14 font-medium leading-[22px] text-black/90 break-words">
                        {item.label}
                      </dt>
                      <dd className="mt-2 text-14 font-normal leading-[22px] text-black/60 whitespace-pre-wrap break-words">
                        {item.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          </div>
        </div>

        {/* Global Distribution */}
        <div
          className={clsx(
            'py-15 px-6',
            'laptop:py-40 laptop:px-0',
            'relative w-full transition-all duration-600 ease-in-out',
            isBlueTheme ? 'bg-pdblue' : 'bg-white'
          )}
        >
          <div className={clsx('w-full max-w-300 mx-auto', 'flex flex-col')}>
            <div className={clsx('flex flex-col', 'laptop:flex-row', 'mb-6 laptop:mb-0')}>
              <h2
                className={clsx(
                  'w-full text-28 pb-3',
                  'laptop:w-100 laptop:text-32 laptop:pb-0',
                  'flex-initial mt-4 font-semibold',
                  'whitespace-pre-line',
                  'transition-all duration-600 ease-in-out',
                  isBlueTheme ? 'text-white/90' : 'text-black/90'
                )}
              >
                {globalDistribution.title}
              </h2>
              <div
                className={[
                  'w-full',
                  'laptop:w-200',
                  'grid grid-cols-2 laptop:grid-cols-3 gap-2 laptop:gap-4',
                  'flex-initial pt-4 border-t border-c2',
                ].join(' ')}
              >
                {globalDistribution.tops?.map((top) => (
                  <div key={top.desc} className={clsx('flex flex-col gap-2', 'my-5 laptop:my-0')}>
                    <p
                      className={clsx(
                        'text-28',
                        'laptop:text-48',
                        'font-semibold',
                        isBlueTheme ? 'text-white/90' : 'text-black/90'
                      )}
                    >
                      <Count end={top.count} />
                    </p>
                    <p
                      className={clsx('text-base whitespace-pre-wrap', isBlueTheme ? 'text-white/60' : 'text-black/60')}
                    >
                      {top.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            {!isMobileDevice && (
              <>
                <div className={clsx('w-full mt-16 mb-20 [aspect-ratio:2/1]')}>
                  <WorldMap mediaUrl={globalDistribution.mediaUrl} locationItems={globalDistribution.locationItems} />
                </div>
                {globalDistribution.brandDesc && (
                  <p
                    className={clsx('text-black/40 text-xs self-end mt-4 mb-20')}
                    dangerouslySetInnerHTML={{
                      __html: globalDistribution.brandDesc.replace(/\[(\d+)\]/g, (_, num) => `<sup>[${num}]</sup>`),
                    }}
                  />
                )}
              </>
            )}
            {globalDistribution.productMediaUrl && (
              <div className={clsx('px-4 laptop:px-70px')}>
                <Marquee gradient={false}>
                  <div className={clsx('w-300 h-10')}>
                    <PdImage
                      src={globalDistribution.productMediaUrl}
                      alt="Global Distribution"
                      width="100%"
                      height="100%"
                    />
                  </div>
                </Marquee>
              </div>
            )}
            {isMobileDevice ? (
              <div className={clsx('rounded-2xl', isBlueTheme ? 'bg-white/20' : 'bg-pdbg')}>
                {globalDistribution.bottoms.map((bottom) => (
                  <div key={bottom.titleMiddle} className={clsx('flex gap-x-5 p-4')}>
                    <h1 className={clsx('text-24 font-semibold', isBlueTheme ? 'text-white/90' : 'text-black/90')}>
                      {bottom.titleTop}
                    </h1>
                    <div className={clsx()}>
                      <p className={clsx('text-24 font-semibold', isBlueTheme ? 'text-white/90' : 'text-black/90')}>
                        {bottom.titleMiddle}
                      </p>
                      <p
                        className={clsx('text-12 whitespace-pre-wrap', isBlueTheme ? 'text-white/60' : 'text-black/60')}
                      >
                        {bottom.descBottom}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={clsx(
                  'flex justify-between rounded-[100px] transition-all duration-400 ease-in-out flex-wrap',
                  isBlueTheme ? 'bg-white/20' : 'bg-pdbg'
                )}
              >
                {globalDistribution.bottoms.map((bottom) => (
                  <div key={bottom.titleMiddle} className={clsx('flex flex-col flex-1 text-center px-6 py-4 gap-2')}>
                    <p
                      className={clsx(
                        'text-5xl font-medium transition-all duration-400 ease-in-out',
                        isBlueTheme ? 'text-white/90' : 'text-black/90'
                      )}
                    >
                      {bottom.titleTop}
                    </p>
                    <p
                      className={clsx(
                        'text-xl font-semibold leading-7 transition-all duration-400 ease-in-out',
                        isBlueTheme ? 'text-white/90' : 'text-black/90'
                      )}
                    >
                      {bottom.titleMiddle}
                    </p>
                    <p
                      className={clsx(
                        'text-xs leading-5 transition-all duration-400 ease-in-out whitespace-pre-wrap',
                        isBlueTheme ? 'text-white/60' : 'text-black/60'
                      )}
                    >
                      {bottom.descBottom}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 发展历程 */}
        <div className={clsx('w-full h-fit')} ref={historyRef}>
          <History data={{ title: data?.devHistoryTitle, history: data?.devHistory }} isBlueTheme={isBlueTheme} />
        </div>
        {/* 企业文化*/}
        <div
          className={[
            'py-15 px-6',
            'laptop:py-30 laptop:px-0',
            'relative w-full',
            'transition-all duration-600 ease-in-out',
            isBlueTheme ? 'bg-pdblue' : 'bg-white',
          ].join(' ')}
          ref={cultureRef}
        >
          <div className={['flex-col', 'laptop:flex-row', 'w-full max-w-300 mx-auto flex'].join(' ')}>
            <h2
              className={[
                'w-full text-28 pb-3',
                'laptop:w-100 laptop:text-32 laptop:pb-0',
                'flex-initial mt-4 font-semibold',
                'transition-all duration-600 ease-in-out',
                isBlueTheme ? 'text-white/90' : 'text-black/90',
              ].join(' ')}
            >
              {data?.corporateCulture?.title}
            </h2>

            <div className={['w-full', 'laptop:w-200', 'flex-initial'].join(' ')}>
              <div className={clsx('flex flex-col gap-y-6 laptop:gap-y-12')}>
                {data?.corporateCulture?.items.map((item) => (
                  <article key={item.title}>
                    <h2
                      className={clsx(
                        'text-sm font-semibold laptop:text-xl',
                        'transition-all duration-600 ease-in-out',
                        isBlueTheme ? 'text-white/60' : 'text-black/60'
                      )}
                    >
                      {item.title}
                    </h2>
                    <p
                      className={clsx(
                        'text-xl whitespace-pre-wrap',
                        'laptop:text-32',
                        'font-semibold',
                        'transition-all duration-600 ease-in-out',
                        isBlueTheme ? 'text-white/90' : 'text-black/90'
                      )}
                    >
                      {item.desc}
                    </p>
                  </article>
                ))}
              </div>
              <ul
                className={['gap-2 mt-8', 'laptop:gap-4 laptop:mt-15', 'grid grid-cols-2 laptop:grid-cols-4'].join(' ')}
              >
                {data?.corporateCulture?.icons.map((culture) => (
                  <li key={culture.title} className={['p-4', 'flex flex-col items-center border border-d8'].join(' ')}>
                    <div
                      className={['relative w-22 flex-none'].join(' ')}
                      style={{
                        aspectRatio: '1 / 1',
                      }}
                    >
                      {culture && (
                        <Image
                          alt={culture.title}
                          src={culture.mediaUrl}
                          layout="fill"
                          objectFit={'contain'}
                          objectPosition={'center'}
                          quality={100}
                          unoptimized
                        />
                      )}
                    </div>
                    <h5 className="w-full text-sm text-black/60 font-semibold text-center mt-2 break-words">
                      {culture.title}
                    </h5>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* 品牌荣誉 */}
        <div className={['py-15 px-6', 'laptop:py-30 laptop:px-0', 'relative w-full bg-gray-100'].join(' ')}>
          <div className={['flex-col', 'laptop:flex-row', 'w-full max-w-300 mx-auto flex'].join(' ')}>
            <div className="w-full laptop:w-100 mt-4">
              <h2
                className={[
                  'w-full text-28 pb-8',
                  'laptop:w-100  laptop:text-32 laptop:pb-4',
                  'flex-initial  text-black/90 font-semibold',
                ].join(' ')}
              >
                {data?.brandHonor?.title}
                <br />
              </h2>
            </div>

            <div className={['w-full', 'laptop:w-200', 'flex-initial'].join(' ')}>
              <ul className={['gap-2', 'laptop:gap-4', 'flex flex-wrap', 'justify-between'].join(' ')}>
                {data?.brandHonor?.items.map((award, index) => (
                  <li
                    key={index}
                    className={[
                      'w-103px py-6 bg-white',
                      'laptop:w-187px laptop:py-11',
                      !isZH && 'laptop:py-12 laptop:px-4 !px-0  h-103px laptop:h-187px',
                      isZH && 'laptop:h-60 laptop:px-0',
                      'flex flex-col gap-4 laptop:gap-4 items-center overflow-hidden',
                    ].join(' ')}
                  >
                    <div
                      className={['h-54px', 'laptop:h-24', 'relative w-full flex justify-center items-center'].join(
                        ' '
                      )}
                    >
                      <Image
                        alt={award.title}
                        src={award.mediaUrl}
                        layout="fill"
                        width="100%"
                        height="100%"
                        objectFit={'contain'}
                        objectPosition={'center'}
                        quality={100}
                        unoptimized
                      />
                    </div>
                    {award.title && (
                      <span className={['text-xs text-center text-black/60 px-3 whitespace-pre-line'].join(' ')}>
                        {award?.title}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {!isMobileDevice && data?.bottomRemark && data?.bottomRemark.length > 0 && (
          <div className={clsx('w-full h-fit bg-gray-200')}>
            <div className={clsx('max-w-300 mx-auto py-15 flex flex-col gap-y-4')}>
              {data?.bottomRemark.map((disclaimer) => (
                <div key={disclaimer.title} className={clsx('flex flex-col gap-y-1')}>
                  <h2 className="text-black/60 text-xs font-bold">{disclaimer.title}</h2>
                  <p className="text-black/60 text-xs whitespace-pre-wrap">{disclaimer.desc}</p>
                </div>
              ))}
              <div className="h-px w-full bg-BBC3CE" />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export const getStaticProps = withSEO(
  async ({ locale }: GetStaticPropsContext) => {
    const messages = retriveMessages(['company'], locale as LocaleType)
    const initData = await getAboutUs(locale as string)
    return {
      props: {
        messages,
        initData: initData || {},
        seoData: (initData as any)?.seoData,
      },
      revalidate: 60,
    }
  },
  () => '/company'
)

About.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'website'}
    >
      {page}
    </PageLayout>
  )
}

export default About
